import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, type ChakraProps, Grid, GridItem, Heading, HStack, IconButton, Input, Link, ListItem, Stack, Text, Tooltip, UnorderedList } from "~/foundation/Chakra";
import { useForm } from "react-hook-form";
import { type ChatBotProps } from "./index";
import bgImage from "~/foundation/Assets/svg/graphics/alia-graphics.svg";
import { Icon } from "../Icon";
import { Loader } from "../Loading";
import ChatMarkdown from "./ChatMarkdown";
import {
	type AliaChatFeedback,
	type AliaChatMessage,
	type AliaChatQuestion
} from "~/foundation/SitecoreContent/generated-types";
import { useTranslation } from "~/foundation/Dictionary";
import aliaCurve from "~/foundation/Assets/svg/graphics/alia-curve.svg";
import { type SearchDictionary } from "~/feature/Search/dictionary"; //eslint-disable-line
import { aliaChatService } from "~/foundation/SitecoreContent/services/aliaChatService.ts";
import xss from "xss";
import { richTextXssSettings } from '~/foundation/Settings/xssSettings';

type FormValues = {
	question: string;
};

type MessageStatus = "in" | "out" | "error";
type ChatMessage = AliaChatMessage & { status: MessageStatus };

async function makeQuery(question: AliaChatQuestion): Promise<ChatMessage> {
	const requestBody = {
		question: question.question,
		chatlogId: question.chatlogId,
	};

	// We make our own client side timestamp on outgoing messages.
	const timestamp = new Date().toLocaleTimeString();
	const response = await aliaChatService.chatQuestion(requestBody);

	let out: ChatMessage;
	if (response.data) {
		out = { ...response.data, status: "in" };
	} else {
		out = { response: `${response.error} [${response.statusCode}]`, status: "error", chatlogId: "", timestamp, sources: [] };
	}
	return out;
}

function ChatBot({ isRtl = false, initialQuestion, largeContentPadding = true, formatMessageQuestions = false, settings }: ChatBotProps) {
	const { register, handleSubmit, setFocus, resetField, formState } = useForm<FormValues>();

	const [history, setHistory] = useState<ChatMessage[]>([]);
	const [chatlogId, setChatlogId] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [feedBackSet, setFeedbackSet] = useState(false);

	const [t] = useTranslation<SearchDictionary>();

	const getMessageStyle = (status: ChatMessage["status"]): ChakraProps => {
		switch (status) {
			case "in":
				return {
					bg: "white",
					color: "black",
					position: "relative",
					_after: {
						content: '""',
						position: "absolute",
						top: "1.6em",
						left: "-5px",
						background: `url("${aliaCurve}")`,
						backgroundRepeat: "no-repeat",
						backgroundSize: "contain",
						w: "22px",
						h: "39px",
					}
				};
			case "out":
				return { bg: "primary.aramcoBlue", color: "white" };
			case "error":
				return { bg: "secondary.red100", color: "white" };
			default:
				return { bg: "primary.aramcoGrey", color: "white" };
		}
	}

	const handleQuestion = useCallback(async (question: AliaChatQuestion) => {
		history.push({ response: question.question, status: "out", chatlogId, timestamp: new Date().toLocaleTimeString(), sources: [] });
		setHistory(history);
		resetField("question");
		setFocus("question");
		setIsLoading(true);
		makeQuery(question).then((message) => {
			setChatlogId(message.chatlogId);
			message.timestamp = new Date().toLocaleTimeString();
			history.push(message);
			setHistory(history);
			setFeedbackSet(false);
			setIsLoading(false);
		}).finally(() => {
			setTimeout(() => {
				inputRef.current && inputRef.current.scrollIntoView({ behavior: "smooth" })
			}, 10);
		});
	}, [chatlogId]);
	const handleFeedback = useCallback(async (feedback: AliaChatFeedback) => {
		await aliaChatService.feedbackMessage(feedback);
		setFeedbackSet(true);
	}, [chatlogId]);

	const onSubmit = handleSubmit(data => {
		if (!data?.question) return;
		const question: AliaChatQuestion = { question: data.question, chatlogId };
		handleQuestion(question).then(() => {
			inputRef.current && inputRef.current.scrollIntoView({ behavior: "smooth" })
		});
	});

	useEffect(() => {
		if (initialQuestion && history.length === 0) {
			handleQuestion({ question: initialQuestion, chatlogId });
		}
		setFocus("question");
	}, []);

	const inputRef = useRef<HTMLDivElement>(null);

	return (
		<Stack
			gap={6}
			bg="primary.darkBlue"
			color="primary.white"
			px={largeContentPadding ? ["var(--chakra-sizes-pageMarginSM)", null, null, "16"] : ["1.5rem", null, null, "2rem"]}
			pt={largeContentPadding ? ["6", null, null, "12"] : ["1.5rem", null, null, "2rem"]}
			pb={largeContentPadding ? ["6", null, null, "8"] : ["1.5rem", null, null, "2rem"]}
			h="full"
			roundedStart={{ base: 0, md: "lg" }}
			overflowY="auto"
			position="relative"
		>
			{history.length === 0 ? (
				<Box>
					<Stack
						justifyContent="center"
						ps={{ base: 0, md: "53%" }}
						bgImage={{ base: "none", md: bgImage }}
						h={{ base: "auto", md: "500px" }}
						bgRepeat="no-repeat"
						bgPos={isRtl ? "left center" : "right center"}
					>
						<Heading as="h1" fontSize="4xl" lineHeight={1}>{settings.introTitle}</Heading>
						<Text fontSize="lg">{settings.introDescription}</Text>
					</Stack>
					<Grid templateColumns="repeat(2, 1fr)" gap={{ base: 4, md: 12 }} my={12} fontSize={{ base: "xs", md: "sm" }}>
						{settings.introCards && settings.introCards.map((card, index) => (
							<GridItem colSpan={{ base: 2, sm: 1 }} key={`alia-intro-card-${index}`}>
								<Text fontWeight="bold">{card.title}</Text>
								<Text>{card.description}</Text>
							</GridItem>
						))}
					</Grid>
				</Box>
			) : (
				<Stack
					px={{ base: 6, md: 16 }}
					pt={{ base: 4, md: 12 }}
					pb={{ base: 4, md: 0 }}
					zIndex={1}
					bgColor={{ base: "primary.darkBlue", md: "transparent" }}
					w={{ base: "100%", md: "50%" }}
					gap={1}
					position={{ base: "fixed", md: "absolute" }}
					top={0}
					insetStart={0}
				>
					<Text fontSize="xs" fontWeight={400} display="flex" alignItems="center"><Icon w={8} variant="Alia" /> {settings.introTitle}</Text>
					{settings?.terms && (<Box fontSize="2xs" sx={{
						a: { color: "primary.aramcoLinkBlue" }
					}} dangerouslySetInnerHTML={{ __html: xss(settings.terms, richTextXssSettings) }} />)}
				</Stack>
			)}

			{history.length &&
				<Stack
					gap={6}
					pb={3}
					pt={largeContentPadding ? { base: 24, md: 8 } : { base: 12, md: 0 }}
					position="relative"
				>
					{history.map((message, i) => (
						<HStack key={i} justifyContent={message.status === 'out' ? "flex-end" : "flex-start"}>
							<Box px={6} {...getMessageStyle(message.status)} borderRadius="xl" fontSize="sm" fontWeight={300} maxW={i === 0 ? "60%" : "full"}>
								<ChatMarkdown formatMessageQuestions={i === 1 && formatMessageQuestions} handleQuestion={handleQuestion} chatLogId={message.chatlogId}>{message.response}</ChatMarkdown>
								{message.sources.length > 0 && (
									<Box my={5}>
										<Text fontWeight="bold" mb={3}>{t("search.askAliaSources")}:</Text>
										<UnorderedList my={0} mx={0}>
											{message.sources.map(({ url, title }) => (
												<ListItem key={url} my={1} listStyleType="none" display="flex" gap={3}>
													<Icon color="primary.aramcoLinkBlue" w={4} variant="ArrowRight" />
													<Link color="primary.aramcoLinkBlue" href={url}>{title}</Link>
												</ListItem>
											))}
										</UnorderedList>
									</Box>
								)}
							</Box>
						</HStack>
					))}
					{isLoading && (
						<HStack justifyContent="flex-start">
							<Box px={10} py={6} {...getMessageStyle("in")} borderRadius="xl">
								<Loader />
							</Box>
						</HStack>
					)}
				</Stack>
			}

			{chatlogId && !feedBackSet && (
				<HStack justifyContent="flex-end">
					<HStack gap={6}>
						<Tooltip label={t("search.askAliaFeedbackGood")} aria-label={t("search.askAliaFeedbackGood")} bgColor="lightgray" hasArrow px={3} py={2} borderRadius="sm">
							<IconButton icon={<Icon w={6} variant="ThumbUp" />} onClick={() => {
								handleFeedback({ feedback: "GOOD", chatlogId })
							}} aria-label="This is good" />
						</Tooltip>
						<Tooltip label={t("search.askAliaFeedbackBad")} aria-label={t("search.askAliaFeedbackBad")} bgColor="lightgray" hasArrow px={3} py={2} borderRadius="sm">
							<IconButton icon={<Icon w={6} variant="ThumbDown" />} onClick={() => {
								handleFeedback({ feedback: "BAD", chatlogId })
							}} aria-label="This is bad"
							/>
						</Tooltip>
					</HStack>
				</HStack>
			)}

			<Box mt="auto">
				<form onSubmit={onSubmit}>
					<Box
						borderRadius="full"
						bg="white"
						display="flex"
						ref={inputRef}
					>
						<Input
							{...register("question", { maxLength: { value: 250, message: t("search.askAliaMaxLengthError") } })}
							type="search"
							variant="unstyled"
							placeholder={settings.inputPlaceholderText}
							fontSize="xs"
							fontWeight={300}
							color="primary.darkBlue"
							py={3}
							px={5}
							maxLength={251}
						/>
						<Button onClick={onSubmit} me={5} aria-label={t("search.searchLabel")}>
							<Icon variant="Alia" w={8} color="darkblue" />
						</Button>
					</Box>
					{formState?.errors.question && <Text size="2xs" color="red.300">{formState?.errors.question.message}</Text>}
				</form>
			</Box>
		</Stack>
	);
}

export default ChatBot;
